import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * ColossalAdversaryPattern - Multi-segment boss fight tracker for GM Dashboard.
 *
 * Used by Colossus of the Drylands.
 *
 * Config properties:
 * - displayName: pattern title
 * - icon: emoji icon
 * - colorClass: Tailwind classes
 * - segmentTypes: available segment types
 * - spotlightCost: Fear per additional segment spotlight
 *
 * Character state key: 'colossalAdversary' stores the active colossus.
 */

const SEGMENT_STATES = {
  ACTIVE: 'active',
  BROKEN: 'broken',
  DESTROYED: 'destroyed',
};

const STATE_COLORS = {
  active: 'bg-green-900/30 border-green-600',
  broken: 'bg-yellow-900/30 border-yellow-600',
  destroyed: 'bg-red-900/30 border-red-600',
};

const STATE_LABELS = {
  active: 'Active',
  broken: 'Broken',
  destroyed: 'Destroyed',
};

const ColossalAdversaryPattern = ({
  config,
  character,
  updateCharacter,
  frameStaticData,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('tracker'); // tracker | setup | reference
  const [showSetup, setShowSetup] = useState(false);

  // Setup form state
  const [colossusName, setColossusName] = useState('');
  const [segments, setSegments] = useState([]);
  const [newSegmentName, setNewSegmentName] = useState('');
  const [newSegmentHP, setNewSegmentHP] = useState(5);
  const [newSegmentDifficulty, setNewSegmentDifficulty] = useState(14);
  const [newSegmentFatal, setNewSegmentFatal] = useState(false);
  const [newSegmentChain, setNewSegmentChain] = useState('');

  // Active colossus from character state
  const colossusState = character?.colossalAdversary || null;

  // Reference data
  const exampleColossi = frameStaticData?.customMechanics?.exampleColossi || [];
  const childrenOfGodfell = frameStaticData?.customMechanics?.childrenOfGodfell || [];
  const featureRef = frameStaticData?.customMechanics?.colossalFeatures || {};

  // ---------------------------------------------------------------------------
  // Setup actions
  // ---------------------------------------------------------------------------

  const handleAddSegment = useCallback(() => {
    if (!newSegmentName.trim()) return;

    setSegments(prev => [...prev, {
      id: `seg-${Date.now()}`,
      name: newSegmentName.trim(),
      maxHP: newSegmentHP,
      currentHP: newSegmentHP,
      difficulty: newSegmentDifficulty,
      state: SEGMENT_STATES.ACTIVE,
      fatal: newSegmentFatal,
      chain: newSegmentChain || null,
      pcsOnSegment: [],
    }]);

    setNewSegmentName('');
    setNewSegmentHP(5);
    setNewSegmentDifficulty(14);
    setNewSegmentFatal(false);
    setNewSegmentChain('');
  }, [newSegmentName, newSegmentHP, newSegmentDifficulty, newSegmentFatal, newSegmentChain]);

  const handleRemoveSegment = useCallback((segId) => {
    setSegments(prev => prev.filter(s => s.id !== segId));
  }, []);

  const handleStartColossus = useCallback(() => {
    if (!colossusName.trim() || segments.length === 0) {
      toast.error('Need a name and at least one segment');
      return;
    }

    const newColossus = {
      name: colossusName.trim(),
      segments: segments,
      startedAt: new Date().toISOString(),
      fear: 0,
    };

    updateCharacter({ ...character, colossalAdversary: newColossus });
    toast.success(`${colossusName} has risen!`);
    setShowSetup(false);
    setActiveTab('tracker');
  }, [colossusName, segments, character, updateCharacter]);

  const handleLoadExample = useCallback((example) => {
    setColossusName(example.name);
    const segs = (example.segments || []).map((name, i) => {
      // Parse "Arm x2" format
      const match = name.match(/^(.+)\s+x(\d+)$/);
      if (match) {
        return Array.from({ length: parseInt(match[2]) }, (_, j) => ({
          id: `seg-${Date.now()}-${i}-${j}`,
          name: `${match[1]} ${j + 1}`,
          maxHP: 3,
          currentHP: 3,
          difficulty: 14,
          state: SEGMENT_STATES.ACTIVE,
          fatal: false,
          chain: match[1],
          pcsOnSegment: [],
        }));
      }
      return [{
        id: `seg-${Date.now()}-${i}`,
        name,
        maxHP: name === 'Head' || name === 'Torso' ? 5 : name === 'Body' ? 0 : 3,
        currentHP: name === 'Head' || name === 'Torso' ? 5 : name === 'Body' ? 0 : 3,
        difficulty: name === 'Head' ? 16 : 14,
        state: SEGMENT_STATES.ACTIVE,
        fatal: name === 'Head' || name === 'Torso',
        chain: null,
        pcsOnSegment: [],
      }];
    }).flat();

    setSegments(segs);
    setShowSetup(true);
    toast.success(`Loaded ${example.name} template`);
  }, []);

  // ---------------------------------------------------------------------------
  // Tracker actions
  // ---------------------------------------------------------------------------

  const updateSegment = useCallback((segId, updates) => {
    if (!colossusState) return;

    const newSegments = colossusState.segments.map(seg =>
      seg.id === segId ? { ...seg, ...updates } : seg
    );

    updateCharacter({
      ...character,
      colossalAdversary: { ...colossusState, segments: newSegments },
    });
  }, [colossusState, character, updateCharacter]);

  const handleDamageSegment = useCallback((segId) => {
    const seg = colossusState?.segments.find(s => s.id === segId);
    if (!seg || seg.state === SEGMENT_STATES.DESTROYED) return;

    const newHP = Math.max(0, seg.currentHP - 1);
    const newState = newHP === 0 ? SEGMENT_STATES.DESTROYED : seg.state;

    updateSegment(segId, { currentHP: newHP, state: newState });

    if (newHP === 0) {
      toast.error(`${seg.name} DESTROYED!`, { icon: '💥' });

      // Check defeat conditions
      const updatedSegments = colossusState.segments.map(s =>
        s.id === segId ? { ...s, currentHP: 0, state: SEGMENT_STATES.DESTROYED } : s
      );

      const fatalDestroyed = updatedSegments.some(s => s.fatal && s.state === SEGMENT_STATES.DESTROYED);
      if (fatalDestroyed) {
        toast.success(`${colossusState.name} has been DEFEATED!`, { icon: '🏆', duration: 5000 });
      }

      // Check chain defeat
      const chains = {};
      for (const s of updatedSegments) {
        if (s.chain) {
          if (!chains[s.chain]) chains[s.chain] = { total: 0, destroyed: 0 };
          chains[s.chain].total++;
          if (s.state === SEGMENT_STATES.DESTROYED || s.id === segId) chains[s.chain].destroyed++;
        }
      }
      for (const [chainName, counts] of Object.entries(chains)) {
        if (counts.total > 0 && counts.destroyed >= counts.total) {
          toast.success(`Chain "${chainName}" complete! ${colossusState.name} DEFEATED!`, { icon: '🏆', duration: 5000 });
        }
      }
    }
  }, [colossusState, updateSegment]);

  const handleToggleBroken = useCallback((segId) => {
    const seg = colossusState?.segments.find(s => s.id === segId);
    if (!seg || seg.state === SEGMENT_STATES.DESTROYED) return;

    const newState = seg.state === SEGMENT_STATES.BROKEN
      ? SEGMENT_STATES.ACTIVE
      : SEGMENT_STATES.BROKEN;
    updateSegment(segId, { state: newState });
  }, [colossusState, updateSegment]);

  const handleEndColossus = useCallback(() => {
    updateCharacter({ ...character, colossalAdversary: null });
    toast.success('Colossus encounter ended');
  }, [character, updateCharacter]);

  const handleAddFear = useCallback(() => {
    if (!colossusState) return;
    updateCharacter({
      ...character,
      colossalAdversary: { ...colossusState, fear: (colossusState.fear || 0) + 1 },
    });
  }, [colossusState, character, updateCharacter]);

  const handleSpendFear = useCallback(() => {
    if (!colossusState || (colossusState.fear || 0) <= 0) return;
    updateCharacter({
      ...character,
      colossalAdversary: { ...colossusState, fear: colossusState.fear - 1 },
    });
    toast.success('Fear spent to spotlight additional segment');
  }, [colossusState, character, updateCharacter]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-red-400 mb-1">
          {config.icon || '🏔️'} {colossusState?.name || config.displayName || 'Colossal Adversary'}
        </h3>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {['tracker', 'setup', 'reference'].map((tab) => (
          <DomainCardButton
            key={tab}
            onClick={() => { setActiveTab(tab); if (tab === 'setup') setShowSetup(true); }}
            variant={activeTab === tab ? 'active' : 'secondary'}
            className="flex-1 capitalize"
          >
            {tab}
          </DomainCardButton>
        ))}
      </div>

      {/* Tracker Tab */}
      {activeTab === 'tracker' && (
        <>
          {!colossusState ? (
            <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-600 text-center">
              <p className="text-gray-400 mb-3">No active colossus. Set one up first.</p>
              <DomainCardButton onClick={() => setActiveTab('setup')} variant="primary">
                Setup Colossus
              </DomainCardButton>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Fear tracker */}
              <div className="flex items-center justify-between bg-gray-800/50 p-3 rounded-lg border border-gray-600">
                <div>
                  <span className="text-sm text-gray-300">Fear: </span>
                  <span className="text-lg font-bold text-red-400">{colossusState.fear || 0}</span>
                </div>
                <div className="flex gap-2">
                  <DomainCardButton onClick={handleSpendFear} variant="secondary" className="px-2 py-1" disabled={(colossusState.fear || 0) <= 0}>
                    Spend
                  </DomainCardButton>
                  <DomainCardButton onClick={handleAddFear} variant="attack" className="px-2 py-1">
                    + Fear
                  </DomainCardButton>
                </div>
              </div>

              {/* Segments */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {colossusState.segments.map((seg) => (
                  <div key={seg.id} className={`p-3 rounded-lg border ${STATE_COLORS[seg.state]}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-medium text-gray-200">{seg.name}</span>
                        {seg.fatal && <span className="text-xs text-red-400 ml-2">(Fatal)</span>}
                        {seg.chain && <span className="text-xs text-yellow-400 ml-2">(Chain: {seg.chain})</span>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        seg.state === 'destroyed' ? 'bg-red-600 text-white'
                          : seg.state === 'broken' ? 'bg-yellow-600 text-white'
                            : 'bg-green-600 text-white'
                      }`}>
                        {STATE_LABELS[seg.state]}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="text-gray-400">
                        HP: <span className={`font-bold ${seg.currentHP <= 1 ? 'text-red-400' : 'text-gray-200'}`}>
                          {seg.currentHP}
                        </span>/{seg.maxHP} | Diff: {seg.difficulty}
                      </div>
                      <div className="flex gap-2">
                        {seg.state !== SEGMENT_STATES.DESTROYED && (
                          <>
                            <DomainCardButton
                              onClick={() => handleToggleBroken(seg.id)}
                              variant="warning"
                              className="px-2 py-1"
                            >
                              {seg.state === SEGMENT_STATES.BROKEN ? 'Clear' : 'Break'}
                            </DomainCardButton>
                            <DomainCardButton
                              onClick={() => handleDamageSegment(seg.id)}
                              variant="attack"
                              className="px-2 py-1"
                            >
                              −1 HP
                            </DomainCardButton>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* End encounter */}
              <DomainCardButton onClick={handleEndColossus} variant="attack" className="w-full">
                End Colossus Encounter
              </DomainCardButton>
            </div>
          )}
        </>
      )}

      {/* Setup Tab */}
      {activeTab === 'setup' && (
        <div className="space-y-3">
          {/* Quick load from examples */}
          {exampleColossi.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 mb-1">Quick Load:</p>
              <div className="flex flex-wrap gap-2">
                {exampleColossi.map((ex) => (
                  <DomainCardButton
                    key={ex.name}
                    onClick={() => handleLoadExample(ex)}
                    variant="secondary"
                    className="px-2 py-1 text-xs"
                  >
                    T{ex.tier}: {ex.name.split(',')[0]}
                  </DomainCardButton>
                ))}
              </div>
            </div>
          )}

          {/* Colossus name */}
          <input
            type="text"
            value={colossusName}
            onChange={(e) => setColossusName(e.target.value)}
            placeholder="Colossus name..."
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-gray-200"
          />

          {/* Add segment form */}
          <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600 space-y-2">
            <p className="text-sm font-medium text-gray-300">Add Segment</p>
            <input
              type="text"
              value={newSegmentName}
              onChange={(e) => setNewSegmentName(e.target.value)}
              placeholder="Segment name (Head, Torso, Arm 1...)"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm text-gray-200"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-400">HP</label>
                <input
                  type="number"
                  value={newSegmentHP}
                  onChange={(e) => setNewSegmentHP(parseInt(e.target.value) || 0)}
                  className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm text-gray-200"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400">Difficulty</label>
                <input
                  type="number"
                  value={newSegmentDifficulty}
                  onChange={(e) => setNewSegmentDifficulty(parseInt(e.target.value) || 0)}
                  className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm text-gray-200"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs text-gray-400">
                <input type="checkbox" checked={newSegmentFatal} onChange={(e) => setNewSegmentFatal(e.target.checked)} className="rounded" />
                Fatal
              </label>
              <input
                type="text"
                value={newSegmentChain}
                onChange={(e) => setNewSegmentChain(e.target.value)}
                placeholder="Chain group (optional)"
                className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-gray-200"
              />
            </div>
            <DomainCardButton onClick={handleAddSegment} variant="primary" className="w-full" disabled={!newSegmentName.trim()}>
              Add Segment
            </DomainCardButton>
          </div>

          {/* Preview segments */}
          {segments.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-gray-400">{segments.length} segments:</p>
              {segments.map((seg) => (
                <div key={seg.id} className="flex items-center justify-between bg-gray-700/50 p-2 rounded text-sm">
                  <span className="text-gray-200">
                    {seg.name} (HP:{seg.maxHP} Diff:{seg.difficulty})
                    {seg.fatal && <span className="text-red-400 text-xs ml-1">Fatal</span>}
                    {seg.chain && <span className="text-yellow-400 text-xs ml-1">Chain:{seg.chain}</span>}
                  </span>
                  <DomainCardButton onClick={() => handleRemoveSegment(seg.id)} variant="secondary" className="px-2 py-1">
                    ×
                  </DomainCardButton>
                </div>
              ))}

              <DomainCardButton onClick={handleStartColossus} variant="attack" className="w-full">
                Start Encounter: {colossusName || 'Unnamed Colossus'}
              </DomainCardButton>
            </div>
          )}
        </div>
      )}

      {/* Reference Tab */}
      {activeTab === 'reference' && (
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {/* Children of Godfell */}
          {childrenOfGodfell.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-300 mb-2">Children of Godfell</p>
              <div className="space-y-1">
                {childrenOfGodfell.map((child, i) => (
                  <div key={i} className="bg-gray-800/50 p-2 rounded text-xs">
                    <span className="text-red-300 font-medium">{child.name}</span>
                    <span className="text-gray-500 ml-2">{child.form}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feature reference */}
          {Object.keys(featureRef).length > 0 && (
            <div className="border-t border-gray-600 pt-3">
              <p className="text-sm font-medium text-gray-300 mb-2">Colossal Features</p>
              {Object.entries(featureRef).map(([category, features]) => (
                <div key={category} className="mb-2">
                  <p className="text-xs text-gray-400 capitalize mb-1">{category}:</p>
                  <div className="flex flex-wrap gap-2">
                    {features.map((f) => (
                      <span key={f} className="px-2 py-0.5 text-xs bg-gray-700 rounded text-gray-300">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>
    </div>
  );
};

export default ColossalAdversaryPattern;
